import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { IconButton } from '@components/iconButton';
import { Button } from '@components/button';
import { Tooltip } from '@components/tooltip/Tooltip';
import { TextField } from '@components/textField';
import { useUpdateQuote } from '@services/quotes';
import { enqueueSnackbar } from 'notistack';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';

import { PenSolid } from '@assets/svgs';
import { ToastType } from '@components/toast';

interface EditableRateVersionProps {
  quoteId: string;
  rateVersion: number | null | undefined;
}

export const EditableRateVersion = ({ quoteId, rateVersion }: EditableRateVersionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const { isAdmin, isPortalAdmin } = useStore((state) => state.userAuth, shallow);
  const { updateQuote, isPending } = useUpdateQuote();

  const canEdit = isAdmin || isPortalAdmin;
  const displayValue = rateVersion !== null && rateVersion !== undefined ? rateVersion.toString() : '-';

  const handleStartEdit = () => {
    setEditValue(displayValue === '-' ? '' : displayValue);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const handleSave = async () => {
    const newVersion = editValue.trim() === '' ? null : Number(editValue);

    // Validate that the input is a valid number
    if (isNaN(newVersion as number)) {
      enqueueSnackbar('Please enter a valid number', {
        variant: ToastType.Error,
        autoHideDuration: 3000,
      });
      return;
    }

    try {
      await updateQuote({
        quoteId,
        rateVersion: newVersion!,
      });

      enqueueSnackbar('Quote Rate Version updated successfully', {
        variant: ToastType.Success,
        autoHideDuration: 3000,
      });

      setIsEditing(false);
      setEditValue('');
    } catch (error: any) {
      // Extract error message from API response
      let errorMessage = 'Failed to update Quote Rate Version';

      try {
        const data = await error?.response?.json();
        if (data?.error?.message) {
          errorMessage = data.error.message;
        }
      } catch {
        // If we can't parse the response, use the default message
        if (error?.message) {
          errorMessage = error.message;
        }
      }

      enqueueSnackbar(errorMessage, {
        variant: ToastType.Error,
        autoHideDuration: 5000,
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <Box
      component='span'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 0,
        m: 0,
        minWidth: 0,
        width: '100%',
        alignItems: 'flex-start',
      }}
    >
      <Typography
        component='span'
        variant='caption'
        sx={{
          display: 'block',
          color: 'text.subdued',
        }}
      >
        Quote Rate Version
      </Typography>

      {isEditing ? (
        <Box component='span' sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          <TextField
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            size='small'
            type='number'
            disabled={isPending}
            autoFocus
            sx={{
              '& .MuiInputBase-root': {
                height: '32px',
              },
            }}
          />
          <Box component='span' sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant='contained'
              onClick={handleSave}
              disabled={isPending}
              size='small'
              sx={{
                padding: 0,
              }}
            >
              Save
            </Button>
            <Button
              variant='text'
              onClick={handleCancel}
              disabled={isPending}
              size='small'
              sx={{
                padding: 0,
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Box component='span' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            component='span'
            variant='bodySmall'
            sx={{
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              lineHeight: 1.4,
            }}
          >
            {displayValue}
          </Typography>
          {canEdit && (
            <Tooltip title='Edit Quote Rate Version'>
              <IconButton aria-label='Edit Quote Rate Version' onClick={handleStartEdit} sx={{ padding: '2px' }}>
                <PenSolid width='12px' height='12px' />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};
